/**
 * Create preset from prepared drops
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState } from 'react';
import { Save, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import type { CreatePresetPayload } from './shared';
import { preparedDropToPresetItemPayload } from './shared/prepared-drop-utils';
import { GroupSelect } from './shared/GroupSelect';
import { PresetFoldersPicker } from './folders';
import { backdropClick } from '@/components/ui';

export interface CreatePresetModalProps {
  countryKey: string;
  drops: PreparedDrop[];
  availableGroupNames?: string[];
  isBusy: boolean;
  onClose: () => void;
  onCreate: (payload: CreatePresetPayload) => void;
}

export const CreatePresetModal: FC<CreatePresetModalProps> = ({
  countryKey,
  drops,
  availableGroupNames = [],
  isBusy,
  onClose,
  onCreate,
}) => {
  const [name, setName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [presetFolderIds, setPresetFolderIds] = useState<string[]>([]);

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed || drops.length === 0) return;
    onCreate({
      name: trimmed,
      groupName: groupName.trim() || undefined,
      folderIds: presetFolderIds.length ? presetFolderIds : undefined,
      items: drops
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
        .map(preparedDropToPresetItemPayload),
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
    >
      <div
        className="flex max-h-[90vh] w-full min-w-[280px] max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Save className="h-4 w-4 shrink-0" />
            Save as preset
          </h3>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex min-h-0 min-w-0 flex-col gap-4 overflow-y-auto p-4">
          <p className="text-sm text-muted-foreground">
            Save {drops.length} drop{drops.length !== 1 ? 's' : ''} as a reusable preset.
          </p>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-muted-foreground">Preset name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Casino Flow"
              maxLength={120}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-muted-foreground">Group (optional)</label>
            <GroupSelect
              value={groupName}
              onChange={setGroupName}
              availableGroupNames={availableGroupNames}
            />
          </div>
          <PresetFoldersPicker
            countryKey={countryKey}
            value={presetFolderIds}
            onChange={setPresetFolderIds}
            disabled={isBusy}
          />
          <div className="flex shrink-0 justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={onClose} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isBusy || !name.trim()}>
              Create preset
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
